import { getScrollableAncestors } from "./themePreviewSelectedOverlayHelpers";

export type AttachSelectedLayoutObserversOptions = {
  shadowRoot: ShadowRoot;
  win: Window;
  /** Danh sách node cần theo dõi (scroll/resize); gọi lại mỗi lần attach. */
  getTargets: () => HTMLElement[];
  onUpdate: () => void;
};

/**
 * Giống `attachSelectedObservers` trong `useThemePreview`: scroll ancestor + ResizeObserver + window scroll/resize.
 * Không gồm RAF — hook hoặc caller tự bơm frame nếu cần.
 */
export function attachSelectedLayoutObservers(
  options: AttachSelectedLayoutObserversOptions
): () => void {
  const { shadowRoot, win, getTargets, onUpdate } = options;

  let selectedScrollAncestors: (Element | Window)[] = [];
  let selectedScrollHandler: ((e: Event) => void) | null = null;
  let selectedResizeObservers: ResizeObserver[] = [];

  const detachObservers = () => {
    if (selectedScrollAncestors.length && selectedScrollHandler) {
      selectedScrollAncestors.forEach((anc) => {
        if (anc === win)
          (anc as Window).removeEventListener("scroll", selectedScrollHandler as EventListener);
        else (anc as Element).removeEventListener("scroll", selectedScrollHandler as EventListener);
      });
    }
    selectedScrollAncestors = [];
    selectedScrollHandler = null;
    selectedResizeObservers.forEach((ro) => {
      try {
        ro.disconnect();
      } catch {
        /* noop */
      }
    });
    selectedResizeObservers = [];
  };

  const attachObservers = () => {
    detachObservers();
    const targets = getTargets();
    if (targets.length === 0) return;

    selectedScrollHandler = () => {
      onUpdate();
    };
    selectedScrollAncestors = getScrollableAncestors(targets[0], shadowRoot, win);
    selectedScrollAncestors.forEach((anc) => {
      if (anc === win)
        (anc as Window).addEventListener("scroll", selectedScrollHandler as EventListener, {
          passive: true,
        });
      else
        (anc as Element).addEventListener("scroll", selectedScrollHandler as EventListener, {
          passive: true,
        });
    });

    const RO = (win as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    if (RO) {
      targets.forEach((t) => {
        const ro = new RO(() => {
          onUpdate();
        });
        selectedResizeObservers.push(ro);
        try {
          ro.observe(t);
        } catch {
          /* noop */
        }
      });
    }
  };

  const onResize = () => {
    onUpdate();
  };

  /** Scroll document đã nằm trong `getScrollableAncestors` (có `win`). */
  win.addEventListener("resize", onResize, { passive: true });

  attachObservers();

  return () => {
    detachObservers();
    win.removeEventListener("resize", onResize);
  };
}
